import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaymentTypeSelector } from './PaymentTypeSelector';
import { StandardPayment } from './StandardPayment';
import { InstallmentPayment, Installment, Recipient } from './InstallmentPayment';
import { CardOnFilePayment } from './CardOnFilePayment';
import { PreAuthPayment } from './PreAuthPayment';
import { ShopProductsPayment } from './ShopProductsPayment';
import { FixedLinksPayment } from './FixedLinksPayment';
import { BulkPaymentUpload } from './BulkPaymentUpload';
import { BulkPaymentListSimplified } from './BulkPaymentListSimplified';
import { RecurringPayment } from './RecurringPayment';
import { InfoIcon, CheckCircleIcon, CalendarIcon, ArrowLeftIcon, ListIcon, RepeatIcon } from 'lucide-react';
import { ShareLinkModal } from './ShareLinkModal';
import { SuccessModal } from './SuccessModal';
export type PaymentType = 'standard' | 'installment' | 'card-on-file' | 'preauth' | 'shop-products' | 'fixed-links' | 'bulk' | 'recurring';
export type SubscriptionMode = 'existing' | 'new';
export function PaymentForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Map URL tab parameter to payment type
  const getInitialPaymentType = (): PaymentType => {
    const tab = searchParams.get('tab');
    const tabMapping: Record<string, PaymentType> = {
      single: 'standard',
      bulk: 'bulk',
      split: 'installment',
      'fixed-links': 'fixed-links',
      charge: 'card-on-file',
      'security-hold': 'preauth',
      shop: 'shop-products',
      recurring: 'recurring'
    };
    return tabMapping[tab || ''] || 'standard';
  };
  const [paymentType, setPaymentType] = useState<PaymentType>(getInitialPaymentType());
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [subscriptionMode, setSubscriptionMode] = useState<SubscriptionMode>('existing');
  const [installmentData, setInstallmentData] = useState<Installment[]>([]);
  const [isMultipleRecipients, setIsMultipleRecipients] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [securityType, setSecurityType] = useState<'public' | 'password' | 'email' | 'sms' | 'document' | 'geo'>('public');
  const [currentPage, setCurrentPage] = useState(1);
  const batchesPerPage = 10;
  // Update payment type when URL parameter changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      const newPaymentType = getInitialPaymentType();
      setPaymentType(newPaymentType);
    }
  }, [searchParams]);
  // Calculate days between now and expiry date
  useEffect(() => {
    if (expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(part => parseInt(part, 10));
      const expiryDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time part to get accurate day difference
      const timeDiff = expiryDateObj.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      setExpiryDays(daysDiff);
    }
  }, [expiryDate]);
  // This is a mock function that would normally get data from the InstallmentPayment component
  // In a real app, you would use a ref or state lifting to get the actual data
  const getInstallmentData = () => {
    // For demonstration purposes, we're returning mock data
    if (paymentType === 'installment') {
      return [{
        id: 1,
        amount: '100.00',
        description: 'First payment',
        dueDate: '',
        recipient: {
          id: 1,
          email: 'recipient1@example.com',
          phone: '+1 (555) 123-4567'
        }
      }, {
        id: 2,
        amount: '200.00',
        description: 'Second payment',
        dueDate: '',
        recipient: {
          id: 2,
          email: 'recipient2@example.com',
          phone: '+1 (555) 987-6543'
        }
      }];
    }
    return [];
  };
  const handleButtonClick = () => {
    if (paymentType === 'card-on-file') {
      // Show success confirmation for card on file
      setShowSuccessModal(true);
    } else if (paymentType === 'bulk') {
      // Navigate to bulk payments page using React Router
      navigate('/bulk-payments');
    } else if (paymentType === 'recurring' && subscriptionMode === 'existing') {
      // Show success confirmation for existing customer subscription
      setShowSuccessModal(true);
    } else {
      // Get data based on payment type
      if (paymentType === 'installment') {
        const data = getInstallmentData();
        setInstallmentData(data);
        // Check if this is a split payment with multiple recipients
        // Only consider it a multi-recipient payment if we have recipients with contact info
        const hasValidRecipients = data.some(item => item.recipient && (item.recipient.email || item.recipient.phone));
        setIsMultipleRecipients(hasValidRecipients && data.length > 1);
      } else {
        // Reset installment data for other payment types
        setInstallmentData([]);
        setIsMultipleRecipients(false);
      }
      // Show share options modal
      setShowShareModal(true);
    }
  };
  const handleViewBatchDetail = (batchId: string) => {
    navigate(`/bulk-payment-detail/${batchId}`);
  };
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
  };
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
  };
  const handleSubscriptionModeChange = (mode: SubscriptionMode) => {
    setSubscriptionMode(mode);
  };
  const handleSecurityTypeChange = (newSecurityType: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => {
    setSecurityType(newSecurityType);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  // Generate payment summary based on current configuration
  const getPaymentSummary = () => {
    const paymentTypeLabels = {
      standard: 'single payment',
      installment: 'split payment',
      'card-on-file': 'saved card payment',
      preauth: 'security hold',
      'shop-products': 'product payment',
      'fixed-links': 'fixed link payment',
      bulk: 'bulk payment',
      recurring: 'recurring subscription'
    };
    const securityLabels = {
      public: 'public access',
      password: 'password protection',
      email: 'email verification',
      sms: 'SMS verification',
      document: 'document verification',
      geo: 'geographic restriction'
    };
    let summary = `Creating a ${paymentTypeLabels[paymentType]}`;
    if (amount) {
      summary += ` for $${amount} ${currency}`;
    }
    if (reference) {
      summary += ` with reference "${reference}"`;
    }
    if (securityType !== 'public') {
      summary += ` and ${securityLabels[securityType]} security`;
    }
    if (expiryDate && paymentType !== 'card-on-file' && paymentType !== 'preauth' && paymentType !== 'fixed-links' && paymentType !== 'recurring') {
      summary += ` that expires on ${expiryDate}`;
    }
    if (paymentType === 'fixed-links') {
      summary += ` that never expires`;
    }
    if (paymentType === 'recurring') {
      summary += ` that bills automatically on a recurring basis`;
    }
    return summary;
  };
  return <div className="space-y-6" data-id="element-2558">
      {/* Clean tabs without containing box */}
      <div className="border-b border-gray-200 pb-0" data-id="element-2559">
        <PaymentTypeSelector activeType={paymentType} onChange={setPaymentType} data-id="element-2560" />
      </div>
      <div className="mt-6" data-id="element-2561">
        {paymentType === 'standard' && <StandardPayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} onSecurityTypeChange={handleSecurityTypeChange} data-id="element-2562" />}
        {paymentType === 'installment' && <InstallmentPayment onExpiryDateChange={handleExpiryDateChange} data-id="element-2563" />}
        {paymentType === 'card-on-file' && <CardOnFilePayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-2564" />}
        {paymentType === 'preauth' && <PreAuthPayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-2565" />}
        {paymentType === 'shop-products' && <ShopProductsPayment onExpiryDateChange={handleExpiryDateChange} data-id="element-2566" />}
        {paymentType === 'fixed-links' && <FixedLinksPayment onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-2567" />}
        {paymentType === 'recurring' && <RecurringPayment onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} onSubscriptionModeChange={handleSubscriptionModeChange} data-id="element-2568" />}
        {paymentType === 'bulk' && <div className="space-y-6" data-id="element-2569">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2570">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2571">
                Upload Payment Data
              </h3>
              <BulkPaymentUpload simplified={true} onBatchCreated={() => {}} data-id="element-2572" />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2573">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2574">
                Recent Payment Batches
              </h3>
              <div className="mb-4 flex justify-between items-center" data-id="element-2575">
                <div className="text-sm text-gray-600" data-id="element-2576">
                  Showing the last {batchesPerPage} batches
                </div>
                <button onClick={() => navigate('/bulk-payments')} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" data-id="element-2577">
                  View Full Batch History
                </button>
              </div>
              <BulkPaymentListSimplified onViewDetail={handleViewBatchDetail} data-id="element-2578" />
              {/* Simple pagination */}
              <div className="mt-4 flex justify-center" data-id="element-2579">
                <nav className="inline-flex rounded-md shadow-sm" data-id="element-2580">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2581">
                    Previous
                  </button>
                  <button onClick={() => handlePageChange(currentPage + 1)} className="px-3 py-1 rounded-r-md border border-gray-300 border-l-0 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-2582">
                    Next
                  </button>
                </nav>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2583">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2584">
                CSV Format Guidelines
              </h3>
              <div className="space-y-4" data-id="element-2585">
                <p className="text-sm text-gray-600" data-id="element-2586">
                  Your CSV file should include the following columns:
                </p>
                <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-2587">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-2588">
                    <thead className="bg-gray-50" data-id="element-2589">
                      <tr data-id="element-2590">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2591">
                          Column
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2592">
                          Required
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2593">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-2594">
                      <tr data-id="element-2595">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2596">
                          customerName
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2597">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2598">
                          Customer's full name
                        </td>
                      </tr>
                      <tr data-id="element-2599">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2600">
                          email
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2601">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2602">
                          Customer's email address
                        </td>
                      </tr>
                      <tr data-id="element-2603">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2604">
                          phone
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2605">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2606">
                          Customer's phone number
                        </td>
                      </tr>
                      <tr data-id="element-2607">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2608">
                          amount
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2609">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2610">
                          Payment amount (numeric only)
                        </td>
                      </tr>
                      <tr data-id="element-2611">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2612">
                          currency
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2613">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2614">
                          Currency code (USD, EUR, etc.)
                        </td>
                      </tr>
                      <tr data-id="element-2615">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2616">
                          description
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2617">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2618">
                          Payment description
                        </td>
                      </tr>
                      <tr data-id="element-2619">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2620">
                          reference
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2621">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2622">
                          Your internal reference
                        </td>
                      </tr>
                      <tr data-id="element-2623">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2624">
                          dueDate
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2625">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2626">
                          Due date (MM/DD/YYYY)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600" data-id="element-2627">
                  The first row of your CSV should contain these column headers
                  exactly as shown above.
                </p>
              </div>
            </div>
          </div>}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex justify-between items-center" data-id="element-2628">
        <div className="flex items-start text-sm text-gray-600" data-id="element-2629">
          {paymentType === 'card-on-file' ? <div className="flex items-center" data-id="element-2630">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-2631" />
              <span data-id="element-2632">The customer will be charged immediately</span>
            </div> : paymentType === 'preauth' ? <div className="flex items-center" data-id="element-2633">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-2634" />
              <span data-id="element-2635">
                The customer will see a temporary authorization on their card
              </span>
            </div> : paymentType === 'bulk' ? <div className="flex items-center" data-id="element-2636">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-2637" />
              <span data-id="element-2638">
                Create multiple payment requests at once by uploading a CSV file
              </span>
            </div> : paymentType === 'recurring' ? <div className="flex items-center" data-id="element-2639">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-2640" />
              <span data-id="element-2641">
                {subscriptionMode === 'existing' ? 'Set up automatic recurring payments for an existing customer' : 'Send a subscription link to start a recurring payment'}
              </span>
            </div> : <div className="flex" data-id="element-2642">
              <InfoIcon size={16} className="mr-2 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-2643" />
              <span data-id="element-2644">{getPaymentSummary()}</span>
            </div>}
        </div>
        {paymentType === 'bulk' ? <button className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" onClick={handleButtonClick} data-id="element-2645">
            Continue to bulk upload
          </button> : <button className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" onClick={handleButtonClick} data-id="element-2646">
            {paymentType === 'card-on-file' ? 'Process charge' : paymentType === 'preauth' ? 'Create pre-authorization' : paymentType === 'recurring' ? subscriptionMode === 'existing' ? 'Create subscription' : 'Create subscription link' : 'Create payment link'}
          </button>}
      </div>
      {/* Share Link Modal */}
      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} recipients={isMultipleRecipients ? installmentData.map(item => item.recipient).filter((recipient): recipient is Recipient => Boolean(recipient && (recipient.email || recipient.phone))) : []} data-id="element-2647" />}
      {/* Success Modal for Card on File and Existing Customer Subscription */}
      {showSuccessModal && <SuccessModal onClose={() => setShowSuccessModal(false)} title={paymentType === 'recurring' ? 'Subscription Created' : 'Payment Processed'} message={paymentType === 'recurring' ? 'The subscription has been successfully created and the customer will be billed according to the schedule.' : "The customer's card has been successfully charged."} data-id="element-2648" />}
    </div>;
}